import { z } from "zod";

/**
 * ANA SAYFA (CLOS-180 `[162]`).
 *
 * ⚠ Ana Sayfa bir ÖZET yüzeyi: her bölüm sınırlı sayıda satır gösteriyor ve kritik kararlar
 * burada verilmiyor. Kartın kuralı *"kritik işlemler detay ekranda tamamlanır"* — bu yüzden
 * bölüm satırları bir aksiyon alanı taşımıyor, yalnız doğru kayda yönlendiriyor.
 */
const bolumSemasi = z.object({ limit: z.number().int().min(1).max(50) });

export const homeConfigSchema = z.object({
  version: z.literal(1),
  sections: z.object({
    pendingTasks: bolumSemasi,
    /** Kritik tarih penceresi: kaç gün ilerisi "yaklaşan" sayılıyor. */
    upcomingDates: bolumSemasi.extend({
      windowDays: z.number().int().min(1).max(365),
      /**
       * ⚠ HANGİ TARİH TÜRLERİ — `types[].critical` BAYRAĞI DEĞİL (CLOS-1190).
       *
       * Bayrak "değişikliği audit'e DEĞERİYLE girer mi" sorusunu cevaplıyor; katalogun
       * kendi $comment'i bunu yazıyor ve `custom` türü (etiketi "Kritik tarih") `false`
       * taşıyor. Bayrağa bağlandığında kullanıcının "Kritik tarih" diye eklediği tarih
       * "Yaklaşan Kritik Tarihler" listesinde HİÇ görünmüyordu — üstelik aynı tarih için
       * "Kritik tarih yaklaşıyor." bildirimi gidiyordu. Ürün kendi kendisiyle çelişiyordu.
       *
       * ⚠ `contractDates.json`'daki `reminders.dateTypes` aynı sebeple ayrı bir liste ve
       * gerekçesini yazmış; bu alan o dersin ikinci uygulaması.
       */
      dateTypes: z.array(z.string().min(1)).min(1),
    }),
    recentContracts: bolumSemasi,
    /** Kapasite uyarısı eşiği: kullanımın hangi oranında "sona yaklaşıyor" denecek. */
    alerts: bolumSemasi.extend({ capacityThresholdRatio: z.number().min(0.1).max(1) }),
  }),
});
export type HomeConfig = z.infer<typeof homeConfigSchema>;

/** Ana Sayfa bölümleri KANONİK SIRADA (kartın amacı bu sırayı sabitlemek). */
export const HOME_SECTIONS = [
  "pendingTasks",
  "upcomingDates",
  "recentContracts",
  "alerts",
] as const;
export type HomeSection = (typeof HOME_SECTIONS)[number];

export const HOME_SECTION_TITLES: Record<HomeSection, string> = {
  pendingTasks: "Bekleyen İşlemler",
  upcomingDates: "Yaklaşan Kritik Tarihler",
  recentContracts: "Son Çalışılan Sözleşmeler",
  alerts: "Önemli Uyarılar",
};

/**
 * ANA SAYFA SAĞLAYICILARI (CLOS-182 `[164]`).
 *
 * ⚠ Satırlar SEÇİLMİŞ ve DAR: Ana Sayfa bir özet yüzeyi, kaynak değil. Kayda ait ayrıntı
 * detay ekranında; buradaki alanlar yalnız "hangi kayıt, ne durumda ve neden burada"
 * sorularını cevaplıyor.
 */
export type HomeUpcomingDate = {
  id: string;
  contractId: string;
  contractTitle: string;
  dateType: string;
  label: string;
  /** Takvim günü (ISO `YYYY-AA-GG`) — saat dilimi sunucuda çözülmüş hâli. */
  day: string;
  standing: "today" | "upcoming";
};

export type HomeRecentContract = {
  contractId: string;
  contractTitle: string;
  status: string;
  /** Kullanıcının bu kayda EN SON dokunduğu an (audit kaydından). */
  lastTouchedAt: string;
};

/**
 * ⚠ Uyarı bir HUKUKİ HÜKÜM DEĞİL: mesaj görülen durumu söylüyor, sonucunu değil.
 * `contractId` boş olan uyarı çalışma alanı düzeyindedir (kapasite) ve bir kayda
 * yönlendirmez.
 */
export const HOME_ALERT_KINDS = [
  "past_critical_date",
  "signature_not_completed",
  "contract_capacity",
] as const;
export type HomeAlertKind = (typeof HOME_ALERT_KINDS)[number];

export type HomeAlert = {
  id: string;
  kind: HomeAlertKind;
  message: string;
  contractId: string | null;
  contractTitle: string | null;
};
